import React, { useState } from "react";
import { Card, Typography, Button, Select } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { COLORS } from "@/utils/constants";

const { Title, Text } = Typography;

type TenderStatus = "Approved" | "Pending review" | "Submitted";

interface TenderDetailPageProps {
  onSubmitTender: () => void;
  onViewReview: (status: TenderStatus) => void;
  tenderName: string;
  tenderAmount: string;
  initialStatus?: TenderStatus;
}

const TenderDetailPage: React.FC<TenderDetailPageProps> = ({
  onSubmitTender,
  onViewReview,
  tenderName,
  tenderAmount,
  initialStatus = "Pending review",
}) => {
  const [status, setStatus] = useState<TenderStatus>(initialStatus);

  // Current tender data
  const tender = {
    name: tenderName || "Tender name goes here",
    sentForReview: new Date().toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
    amount: tenderAmount || "£ 1,346,557.02",
  };

  const handleStatusChange = (value: TenderStatus) => {
    setStatus(value);
  };

  return (
    <div className="max-w-full mx-auto">
      <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
        {/* Header */}
        <div
          className="px-6 pt-6 pb-4"
          style={{ borderBottom: "1px solid #e5e7eb" }}
        >
          <div>
            <Title
              level={3}
              className="mb-1"
              style={{ fontWeight: 600, fontSize: "20px", color: "#111827" }}
            >
              Review & Calculate
            </Title>
            <Text
              type="secondary"
              style={{ fontSize: "14px", color: "#6b7280" }}
            >
              Calculate and review your tender before submission.
            </Text>
          </div>
        </div>

        {/* Tender Detail */}
        <div className="px-6 py-6">
          {/* Single Tender Card - Clickable */}
          <div
            onClick={() => onViewReview(status)}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "16px 20px",
              backgroundColor: "white",
              borderLeft: `4px solid ${COLORS.primary}`,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            className="hover:shadow-md"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f0f7ff";
              e.currentTarget.style.borderColor = COLORS.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Text strong style={{ fontSize: "16px", color: "#111827" }}>
                    {tender.name}
                  </Text>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircleOutlined
                    style={{ color: "#10b981", fontSize: "14px" }}
                  />
                  <Text style={{ fontSize: "13px", color: "#6b7280" }}>
                    Sent for Review:
                  </Text>
                  <Text style={{ fontSize: "13px", color: "#374151" }}>
                    {tender.sentForReview}
                  </Text>
                </div>
                <div style={{ marginTop: "8px" }}>
                  <Text strong style={{ fontSize: "18px", color: "#111827" }}>
                    {tender.amount}
                  </Text>
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <Select
                  value={status}
                  onChange={handleStatusChange}
                  style={{ width: 150 }}
                  options={[
                    {
                      value: "Approved",
                      label: (
                        <span style={{ color: "#52c41a", fontWeight: 500 }}>
                          Approved
                        </span>
                      ),
                    },
                    {
                      value: "Pending review",
                      label: (
                        <span style={{ color: "#faad14", fontWeight: 500 }}>
                          Pending review
                        </span>
                      ),
                    },
                    {
                      value: "Submitted",
                      label: (
                        <span style={{ color: "#1890ff", fontWeight: 500 }}>
                          Submitted
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TenderDetailPage;
